import { message } from "antd";

interface ApiErrorOptions {
  code?: string;
  status?: number;
  detail?: unknown;
}

export class ApiError extends Error {
  code?: string;
  status?: number;
  detail?: unknown;

  constructor(message: string, options: ApiErrorOptions = {}) {
    super(message);
    this.name = "ApiError";
    this.code = options.code;
    this.status = options.status;
    this.detail = options.detail;
  }
}

export const getAuthToken = (tokenKey: "api_key" | "admin_token" = "api_key"): string => {
  const token = localStorage.getItem(tokenKey);
  if (!token) {
    throw new Error(`Missing ${tokenKey} in localStorage`);
  }
  return token;
};

export const buildAuthHeaders = (
  tokenKey: "api_key" | "admin_token" = "api_key",
  contentType = "application/json"
): Record<string, string> => {
  const token = getAuthToken(tokenKey);
  return {
    "Content-Type": contentType,
    Authorization: `Bearer ${token}`,
  };
};

const SOURCE_NOT_CONNECTED_CODE = "SOURCE_NOT_CONNECTED";

const showSourceNotConnectedMessage = (content: string) => {
  message.error({
    content,
    key: SOURCE_NOT_CONNECTED_CODE,
  });
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const getStringField = (value: unknown, field: string): string | undefined => {
  if (!isRecord(value)) {
    return undefined;
  }

  const fieldValue = value[field];
  return typeof fieldValue === "string" && fieldValue.trim() ? fieldValue : undefined;
};

export const handleJsonResponse = async <T = unknown,>(response: Response): Promise<T> => {
  const responseData = await response.json().catch(() => ({}));

  if (!response.ok) {
    const detail = (responseData as { detail?: unknown }).detail;
    const backendMessage =
      (typeof detail === "string" && detail) ||
      getStringField(detail, "message") ||
      (responseData as { message?: string }).message ||
      `HTTP error! status: ${response.status}`;
    const errorCode = getStringField(detail, "code") || (responseData as { code?: string }).code;

    if (errorCode === SOURCE_NOT_CONNECTED_CODE && backendMessage) {
      showSourceNotConnectedMessage(backendMessage);
    }
    throw new ApiError(backendMessage, {
      code: errorCode,
      status: response.status,
      detail,
    });
  }

  const successCode = (responseData as { code?: string }).code;
  const successMessage = (responseData as { message?: string }).message;

  if (successCode === SOURCE_NOT_CONNECTED_CODE && successMessage) {
    showSourceNotConnectedMessage(successMessage);
    throw new Error(successMessage);
  }

  return responseData as T;
};
